import React, { ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { GuardWithLayout } from '../components';
import { RouteKeys, webAppRoutes } from '../constants';
import { NotFoundView } from '../pages/not-found';
import { selectUser } from '../store/reducers';
import { SignIn } from './lazy-components';
import { PrivateRouter } from './private-router';
import { PublicRouter } from './public-router';
import { createRouteItems } from './factories/create-routes-items';
import routesSingletonService from './routes.service';

function RedirectionRouteComponent({
  redirectTo,
}: {
  redirectTo: string;
}): ReactElement {
  const nav = useNavigate();

  useEffect(() => {
    nav(redirectTo, { replace: true });
  }, []);

  return null;
}

export function GTRoutes(): React.ReactElement {
  const user = useSelector(selectUser);
  const filteredRouteItems = useMemo(
    () => routesSingletonService.getAvailableRoutes(user, true),
    [user],
  );

  const routeItems = useMemo(() => createRouteItems(user), [user]);

  const routes = useMemo(() => {
    return routeItems.map(({ path, ...guardProps }) => (
      <Route
        key={path}
        path={path}
        element={<GuardWithLayout {...guardProps} />}
      />
    ));
  }, [routeItems]);

  return (
    <Routes>
      <Route element={<PublicRouter />} path={RouteKeys.user}>
        <Route element={<SignIn />} path={RouteKeys.signIn} />

        <Route path="*" element={<NotFoundView />} />
      </Route>
      {user ? (
        <Route element={<PrivateRouter />} path="/">
          <Route
            path={webAppRoutes.home}
            element={
              <RedirectionRouteComponent
                redirectTo={
                  filteredRouteItems.length
                    ? filteredRouteItems[0].path
                    : routeItems[0].path
                }
              />
            }
          />

          {routes}

          <Route
            path="*"
            element={<GuardWithLayout component={NotFoundView} />}
          />
        </Route>
      ) : (
        <Route
          path="*"
          element={
            <RedirectionRouteComponent redirectTo={webAppRoutes.signIn} />
          }
        />
      )}
    </Routes>
  );
}
